import { useEffect, useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { WishlistProvider } from './context/WishlistContext';
import { useTrackingInit } from './hooks/useTrackingInit';
import { useSettings } from './hooks/usePublicContent';
import { Layout } from './components/Layout';
import { HomePage } from './pages/HomePage';
import { CategoryPage } from './pages/CategoryPage';
import { ProductPage } from './pages/ProductPage';
import { CartPage } from './pages/CartPage';
import { CheckoutPage } from './pages/CheckoutPage';
import { OrderConfirmationPage } from './pages/OrderConfirmationPage';
import { MyOrdersPage } from './pages/MyOrdersPage';
import { WishlistPage } from './pages/WishlistPage';
import { CustomPrintPage } from './pages/CustomPrintPage';
import { FaqPage } from './pages/FaqPage';
import { AboutPage } from './pages/AboutPage';
import { ContactPage } from './pages/ContactPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { ShopAllPage } from './pages/ShopAllPage';
import { BundlesPage } from './pages/BundlesPage';
import { BundleDetailPage } from './pages/BundleDetailPage';
import { LoadingScreen, getLoadingConfig, cacheLoadingConfig } from './components/LoadingScreen';

const API_URL = import.meta.env.VITE_API_URL as string;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function TrackingInit() {
  useTrackingInit();
  return null;
}

// Reads loading-screen config from the API (and localStorage fallback),
// shows the loader while the settings call is in-flight.
function AppShell({ children }: { children: React.ReactNode }) {
  const { data: settings, isLoading } = useSettings();
  // Frozen at mount from localStorage — no FRAMED flash during API fetch
  const [cfg] = useState(() => getLoadingConfig());

  useEffect(() => {
    if (settings?.loading_screen) {
      cacheLoadingConfig(settings.loading_screen);
    }
  }, [settings]);

  useEffect(() => {
    const favicon = settings?.brand?.logos?.favicon;
    if (!favicon) return;
    const url = favicon.startsWith('http') ? favicon : `${API_URL}${favicon}`;
    let link = document.querySelector<HTMLLinkElement>('link[rel~="icon"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = url;
    link.type = url.endsWith('.svg') ? 'image/svg+xml' : 'image/x-icon';
  }, [settings?.brand?.logos?.favicon]);

  return (
    <>
      <LoadingScreen config={cfg} ready={!isLoading} />
      {children}
    </>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastContainer />
      <AppShell>
      <TrackingInit />
      <WishlistProvider>
        <BrowserRouter>
          <Routes>
            <Route element={<Layout />}>
              <Route index element={<HomePage />} />
              <Route path="categories/:slug" element={<CategoryPage />} />
              <Route path="products/:slug" element={<ProductPage />} />
              <Route path="cart" element={<CartPage />} />
              <Route path="checkout" element={<CheckoutPage />} />
              <Route path="orders/:orderNumber" element={<OrderConfirmationPage />} />
              <Route path="my-orders" element={<MyOrdersPage />} />
              <Route path="wishlist" element={<WishlistPage />} />
              <Route path="shop" element={<ShopAllPage />} />
              <Route path="bundles" element={<BundlesPage />} />
              <Route path="bundles/:slug" element={<BundleDetailPage />} />
              <Route path="custom-prints" element={<CustomPrintPage />} />
              <Route path="faqs" element={<FaqPage />} />
              <Route path="about" element={<AboutPage />} />
              <Route path="contact" element={<ContactPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </WishlistProvider>
      </AppShell>
    </QueryClientProvider>
  );
}

export default App;
